// Original chibi residents built from soft rounded shapes, attached to the bones of Kenney's CC0 mini-character rig (D-01), so every look
// (people, animal villagers, the shopkeeper and the curator) shares the rig's walk, run, idle and emote animations.
// Characters pass (2026-10-16, spec §8.2): a big rounded head whose face turns up to the town camera (head.ts), hair styles built as shells
// of locks (hair.ts), a soft body with a shirt over trousers, a collar in the friend accent, arms that hang clear of the body with mitten
// hands, short legs in rounded shoes, and species parts that read from above (species.ts). The whole doll is then baked into one skinned
// mesh (`bakeDoll`), lightly shaded toward the ground like a toy in soft light.
import type { FurPattern, Species } from '@whiskerton/content';
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { isWorldPatched, patchObject } from '../engine/shader/worldPatch';
import { DOLL_ORDER, markDoll } from '../engine/silhouette';
import { addHair } from './hair';
import { face, HEAD, headShape, nose, personEars } from './head';
import { luma, part, shade, toyMaterial } from './part';
import { blob, lathe, smooth } from './shapes';
import { dressAnimal } from './species';

export { HAIR_STYLES } from './hair';
export { toyMaterial } from './part';

export interface DollSpec {
  kind: Species; skin: string; eyes?: string; hair?: string; hairStyle?: string;
  shirt: string; pants: string; shoes?: string; ears?: string; muzzle?: string;
  pattern?: FurPattern; patternColor?: string;
  /** A resident's friend colour (its tag colour, D-21): the collar and the bun's tie. Without one (villagers) the collar is a deeper shirt. */
  accent?: string;
}
export const RIG_HEIGHT = 0.66;
export type DollBone = 'torso' | 'head' | 'arm-left' | 'arm-right' | 'leg-left' | 'leg-right';
export const EYE_STYLES = ['round', 'happy', 'sleepy', 'sparkle'] as const;

export function hideRigMeshes(rig: THREE.Object3D): void {
  rig.traverse((o) => { if ((o as THREE.SkinnedMesh).isSkinnedMesh) o.visible = false; });
}
export function attachPart(rig: THREE.Object3D, bone: DollBone, part: THREE.Object3D): void {
  const b = rig.getObjectByName(bone);
  if (!b) throw new Error(`rig has no bone ${bone}`);
  b.add(part);
}
/** A container group attached to a bone; every rounded part of that bone lives inside it. */
function slot(rig: THREE.Object3D, bone: DollBone): THREE.Group {
  const g = new THREE.Group();
  g.name = `doll-${bone}`;
  attachPart(rig, bone, g);
  return g;
}

export interface BodySpec {
  shirt: string; pants: string; shoes: string;
  /** Hands (skin, or fur for animals). */
  hands: string;
  collar: string;
  /** Widens the torso (1 = a resident; the badger is chubbier). */
  girth?: number;
}
export interface Body { head: THREE.Group; torso: THREE.Group; hands: Record<'left' | 'right', THREE.Mesh> }
/** The chibi body on the rig's bones (rig units; the hips at the torso and leg bones, shoulders at the arm bones, the neck at the head
 *  bone): trousers and a shirt with a rolled hem, a collar, arms angled out from the body with sleeves and mitten hands, short legs in
 *  rounded shoes with a sole. Returns the head slot (tipped chin-up, head.ts HEAD.tilt), the torso slot and the hands. */
export function body(rig: THREE.Object3D, b: BodySpec): Body {
  const girth = b.girth ?? 1;
  const torso = slot(rig, 'torso');
  // A shirt to the hips with a soft rolled hem; the trousers show as the legs and a seat between them.
  const pants = part(torso, lathe([[0, -0.036], [0.05, -0.033], [0.08, -0.02], [0.092, 0.0], [0.0, 0.012]], 10), b.pants, [0, 0, 0], 'body-pants');
  const shirt = part(torso, lathe([[0.078, 0.006], [0.1, -0.009], [0.115, -0.001], [0.119, 0.026], [0.115, 0.06], [0.103, 0.095], [0.086, 0.125],
    [0.063, 0.15], [0.038, 0.166], [0, 0.176]], 16), b.shirt, [0, 0, 0], 'body-shirt');
  for (const m of [pants, shirt]) m.scale.set(girth, 1, girth * 0.93);
  const collar = part(torso, new THREE.TorusGeometry(0.044, 0.016, 4, 12), b.collar, [0, 0.157, 0.006], 'collar');
  collar.rotation.x = Math.PI / 2 + 0.22;

  const hands = {} as Record<'left' | 'right', THREE.Mesh>;
  for (const [bone, sx, side] of [['arm-left', 1, 'left'], ['arm-right', -1, 'right']] as const) {
    const arm = slot(rig, bone);
    arm.position.set(sx * (0.044 + (girth - 1) * 0.1), -0.004, 0);
    arm.rotation.z = sx * 0.42;
    // One rounded sleeve from the shoulder to the elbow, then a short forearm and a mitten hand with its thumb forward.
    part(arm, lathe([[0, -0.072], [0.03, -0.069], [0.038, -0.058], [0.041, -0.03], [0.044, 0], [0.04, 0.022], [0.026, 0.038], [0, 0.044]], 10), b.shirt);
    part(arm, new THREE.CapsuleGeometry(0.027, 0.04, 2, 8), b.hands, [0, -0.085, 0]);
    hands[side] = part(arm, blob(0.037, 0.04, 0.036, 10, 7), b.hands, [0, -0.125, 0.004], `hand-${side}`);
    part(arm, blob(0.015, 0.019, 0.015, 7, 5), b.hands, [-sx * 0.018, -0.112, 0.026]);
  }
  const sole = luma(b.shoes) < 0.45 ? '#EAD6AE' : shade(b.shoes, 0.72);
  for (const [bone, side] of [['leg-left', 'left'], ['leg-right', 'right']] as const) {
    const leg = slot(rig, bone);
    part(leg, new THREE.CapsuleGeometry(0.044, 0.075, 2, 8), b.pants, [0, -0.07, 0]);
    part(leg, blob(0.05, 0.034, 0.072, 10, 7), b.shoes, [0, -0.144, 0.02], `shoe-${side}`);
    part(leg, blob(0.052, 0.011, 0.074, 10, 4), sole, [0, -0.166, 0.02]);
  }
  const head = slot(rig, 'head');
  head.rotation.x = -HEAD.tilt;
  return { head, torso, hands };
}

export function dress(rig: THREE.Object3D, s: DollSpec): void {
  hideRigMeshes(rig);
  const shoes = s.shoes ?? '#5E4733';
  const { head, torso, hands } = body(rig, { shirt: s.shirt, pants: s.pants, shoes, hands: s.skin, collar: s.accent ?? shade(s.shirt, 0.72) });
  rig.userData.hands = hands;
  headShape(head, s.skin);
  if (s.kind === 'person') {
    face(head, { eyes: s.eyes, skin: s.skin });
    nose(head, s.skin);
    if (s.hairStyle !== 'bob' && s.hairStyle !== 'long') personEars(head, s.skin);
    const hair = s.hair ?? '#4A3426';
    addHair(head, hair, s.hairStyle, s.accent ?? shade(hair, 0.6), s.skin);
    return;
  }
  // Animal villagers: hands stay in fur colour; the sheep's face is its `skin` (the ears colour) and its wool the `hair` (fur colour).
  dressAnimal(head, torso, { kind: s.kind, fur: s.skin, ears: s.ears ?? s.skin, muzzle: s.muzzle ?? '#FFF4E2', eyes: s.eyes, wool: s.hair,
    pattern: s.pattern ?? 'none', patternColor: s.patternColor ?? s.ears ?? s.skin });
}

let baked: THREE.MeshStandardMaterial | null = null, bakedToy: THREE.MeshStandardMaterial | null = null;
/** One shared vertex-colour material for every baked doll, matching toyMaterial's matte look (roughness/metalness). It goes through
 *  the same world patch as every world material (patchObject on a carrier mesh), so baked villagers bend with the curved world (§8.1).
 *  Like every doll material it marks the stencil where it shows (engine/silhouette.ts `markDoll`), so a villager in front of you hides
 *  your silhouette. */
export function bakedMaterial(): THREE.MeshStandardMaterial {
  if (!baked) {
    const ref = toyMaterial('#FFFFFF');
    baked = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: ref.roughness, metalness: ref.metalness });
    markDoll(baked);
    patchObject(new THREE.Mesh(new THREE.BufferGeometry(), baked));
  }
  return baked;
}
/** The same look without the world patch, for residents' dolls (and NPCs', the curator's and villagers' indoors): like their toy
 *  materials before baking it stays unpatched, because the Game bends residents on the CPU (game.ts `bend`), and a patched material
 *  would bend them twice. It marks the stencil too. */
export function bakedToyMaterial(): THREE.MeshStandardMaterial {
  if (!bakedToy) {
    const ref = toyMaterial('#FFFFFF');
    bakedToy = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: ref.roughness, metalness: ref.metalness });
    markDoll(bakedToy);
  }
  return bakedToy;
}
const owningBone = (o: THREE.Object3D): THREE.Bone | null => {
  let p = o.parent;
  while (p && !(p as THREE.Bone).isBone) p = p.parent;
  return p as THREE.Bone | null;
};
/** Soft toy shading baked into the colours (rig units, feet at y = 0): a little darker toward the ground and on faces turned down, so a
 *  doll sits in the light on both quality levels without extra passes. */
export const contactShade = (y: number, ny: number) => (1 - 0.17 * (1 - smooth(0, 0.2, y))) * (1 - 0.14 * Math.max(0, -ny));
/** `m`'s geometry in `frame` space, non-indexed, with only position and normal plus a colour attribute (its own vertex colours, if any,
 *  times its material's colour, shaded by `contactShade`) and a rigid skin binding (weight 1 on bone `bone`), so any set of them merges
 *  into one skinned mesh. */
function bakedGeometry(m: THREE.Mesh, frame: THREE.Matrix4, bone: number): THREE.BufferGeometry {
  const src = m.geometry.clone();
  src.applyMatrix4(frame);
  const g = src.index ? src.toNonIndexed() : src;
  if (g !== src) src.dispose();
  const own = g.getAttribute('color');
  for (const k of Object.keys(g.attributes)) if (k !== 'position' && k !== 'normal' && k !== 'color') g.deleteAttribute(k);
  const c = (m.material as THREE.MeshStandardMaterial).color, pos = g.getAttribute('position'), nrm = g.getAttribute('normal'), n = pos.count;
  const rgb = new Float32Array(n * 3), index = new Uint16Array(n * 4), weight = new Float32Array(n * 4);
  for (let i = 0; i < n; i++) {
    const f = contactShade(pos.getY(i), nrm ? nrm.getY(i) : 0);
    const [r, gg, b] = own ? [own.getX(i), own.getY(i), own.getZ(i)] : [1, 1, 1];
    rgb[i * 3] = c.r * r * f; rgb[i * 3 + 1] = c.g * gg * f; rgb[i * 3 + 2] = c.b * b * f;
    index[i * 4] = bone; weight[i * 4] = 1;
  }
  g.setAttribute('color', new THREE.Float32BufferAttribute(rgb, 3));
  g.setAttribute('skinIndex', new THREE.Uint16BufferAttribute(index, 4));
  g.setAttribute('skinWeight', new THREE.Float32BufferAttribute(weight, 4));
  return g;
}
const isBaked = (o: THREE.Object3D): o is THREE.SkinnedMesh => o.name === 'baked' && (o as THREE.SkinnedMesh).isSkinnedMesh === true;
/** Puts `material` on a baked doll; a world-patched one also gets the patched depth material, so the doll's shadow bends too. */
function useBakedMaterial(mesh: THREE.SkinnedMesh, material: THREE.Material): void {
  if (mesh.material === material) return;
  mesh.material = material;
  mesh.customDepthMaterial = undefined;
  mesh.userData.worldPatched = false;
  if (isWorldPatched(material)) patchObject(mesh);
}
/** Draw-call budget (spec §8.7): a dressed doll is ~40 small meshes, and each one is drawn again into the shadow map on `high`. Merge
 *  every visible part (hands included) into ONE vertex-coloured SkinnedMesh rigidly bound to the rig's bones (each vertex weighted 1 to
 *  the bone it hangs from), so the whole doll costs one draw call per pass and still moves with the mixer and the procedural overlays.
 *  The hands become empty anchors at the same place (rig.userData.hands), so holdInHand keeps working. `material` defaults to the
 *  world-patched bakedMaterial (villagers bend on the GPU); a resident's doll passes bakedToyMaterial. Baking a baked doll again only
 *  switches its material. Returns how many visible meshes remain. */
export function bakeDoll(rig: THREE.Object3D, material: THREE.Material = bakedMaterial()): number {
  let done: THREE.SkinnedMesh | null = null;
  rig.traverse((o) => { if (!done && isBaked(o)) done = o; });
  if (done) useBakedMaterial(done, material);
  else bakeParts(rig, material);
  let left = 0;
  rig.traverse((o) => { if ((o as THREE.Mesh).isMesh && o.visible) left++; });
  return left;
}
function bakeParts(rig: THREE.Object3D, material: THREE.Material): void {
  rig.updateMatrixWorld(true);
  // `rig` may be an Avatar's root, whose dressed rig (the one carrying userData.hands) is a child.
  const bones: THREE.Bone[] = [], parts: { mesh: THREE.Mesh; bone: number }[] = [];
  const hands = new Map<THREE.Object3D, { owner: THREE.Object3D; side: string }>();
  rig.traverse((o) => {
    if ((o as THREE.Bone).isBone) bones.push(o as THREE.Bone);
    for (const [side, h] of Object.entries((o.userData.hands ?? {}) as Record<string, THREE.Object3D>)) hands.set(h, { owner: o, side });
  });
  const top = bones.find((b) => !owningBone(b));
  if (!top) return;
  rig.traverse((o) => {
    const m = o as THREE.Mesh, bone = owningBone(m);
    if (m.isMesh && !(m as THREE.SkinnedMesh).isSkinnedMesh && bone && isVisible(m, rig)) parts.push({ mesh: m, bone: bones.indexOf(bone) });
  });
  if (parts.length === 0) return;
  // The skinned mesh sits beside the bone hierarchy (where the rig's own hidden skinned mesh is); its vertices are the parts as they
  // stand now, in its space, and Skeleton/bind take this rest pose as the bind pose.
  const host = top.parent ?? rig, inv = new THREE.Matrix4().copy(host.matrixWorld).invert(), rel = new THREE.Matrix4();
  const geos = parts.map((p) => bakedGeometry(p.mesh, rel.multiplyMatrices(inv, p.mesh.matrixWorld), p.bone));
  const mesh = new THREE.SkinnedMesh(mergeGeometries(geos)!, material);
  for (const g of geos) g.dispose();
  mesh.name = 'baked';
  mesh.castShadow = mesh.receiveShadow = true;
  mesh.renderOrder = DOLL_ORDER;                                   // after the opaque world, so its stencil marks only what still shows
  host.add(mesh);
  mesh.bind(new THREE.Skeleton(bones));
  for (const { mesh: m } of parts) {
    const hand = hands.get(m);
    if (hand) {                                                    // an empty anchor where the hand was: holdInHand puts items here
      const anchor = new THREE.Object3D();
      anchor.name = m.name || `hand-${hand.side}`;
      anchor.position.copy(m.position); anchor.quaternion.copy(m.quaternion); anchor.scale.copy(m.scale);
      m.parent!.add(anchor);
      for (const c of [...m.children]) anchor.add(c);
      (hand.owner.userData.hands as Record<string, THREE.Object3D>)[hand.side] = anchor;
    }
    m.removeFromParent();
    m.geometry.dispose();
  }
  if (isWorldPatched(material)) patchObject(mesh);                 // its shadow bends too (the patched depth material)
}
/** Drawn: it and every parent up to `root` are visible (a part under a hidden group stays as it is). */
function isVisible(o: THREE.Object3D, root: THREE.Object3D): boolean {
  for (let p: THREE.Object3D | null = o; p; p = p === root ? null : p.parent) if (!p.visible) return false;
  return true;
}

/** Frees what dressing and baking made under `root`: the baked mesh's geometry and bone texture, and any part still unbaked. The rig's
 *  own hidden skinned meshes share the kit's geometry and are left alone. */
export function disposeDoll(root: THREE.Object3D): void {
  root.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh) return;
    if (isBaked(m)) { m.geometry.dispose(); m.skeleton.dispose(); }
    else if (!(m as THREE.SkinnedMesh).isSkinnedMesh) m.geometry.dispose();
  });
}

/** Puts `item` in a hand, cancelling the arm's resting tilt so the item keeps its own orientation. */
export function holdInHand(rig: THREE.Object3D, item: THREE.Object3D, side: 'left' | 'right' = 'right'): THREE.Object3D {
  const hand = (rig.userData.hands as Record<'left' | 'right', THREE.Object3D>)[side];
  item.quaternion.premultiply(hand.parent!.quaternion.clone().invert());
  hand.add(item);
  return item;
}

